import axios from 'axios'
import axiosClient from './base'
import type { ConsolidatedPractice } from 'gql/generated-types/custom-graphql'
import { readCapitalAttribution } from '../util/capital'
import type { CapitalEventType, CapitalOfferFields, CapitalOfferSummary } from '../util/capital'
import type { StripeAccountSessionType } from '../util/stripe'

/**
 * Creates a Stripe Capital account session for the given consolidated practice
 * and returns the client secret used to initialize the embedded Capital
 * components. The backend also resolves the connected account's offer so the
 * UI can display the offered amount.
 *
 * @param consolidatedPracticeId - Consolidated practice ID (from
 *   `currentPracticeObject.consolidatedPractice.id`).
 * @returns `data.clientSecret` for Stripe Connect initialization plus
 *   `offerId`/`offerAmount`/`offerStatus`. No `promo` here: this endpoint
 *   prefers a promo-state offer while {@link getCapitalOfferStatus} reports the
 *   newest, so gate rendering on that one.
 */
export const createStripeCapitalAccountSession = (
	consolidatedPracticeId: string
): Promise<{ data: CapitalOfferFields & { clientSecret: string } }> =>
	axiosClient.post(
		`/api/consolidated-practices/${consolidatedPracticeId}/stripe-capital-account-sessions`
	)

/**
 * Creates a Stripe account session of the given `type` for the consolidated
 * practice's connected Stripe account. The backend's
 * `STRIPE_ACCOUNT_SESSION_TYPES` registry owns the embedded-component config
 * and per-type access check, so new session types are added by extending
 * `StripeAccountSessionType` here (and the registry on the backend) — no new
 * endpoint required.
 *
 * @param consolidatedPracticeId - Consolidated practice ID.
 * @param type - Account-session type from {@link StripeAccountSessionType}.
 * @returns `data.clientSecret` for Stripe Connect initialization.
 */
export const createStripeAccountSession = (
	consolidatedPracticeId: string,
	type: StripeAccountSessionType
): Promise<{ data: { clientSecret: string } }> =>
	axiosClient.post(
		`/api/consolidated-practices/${consolidatedPracticeId}/stripe-account-sessions`,
		{ type }
	)

/**
 * Thin wrapper over {@link createStripeAccountSession} that passes the
 * `'payouts'` embedded component type. Used by the Capital-financing payouts
 * dashboard so clinics with active financing see Stripe's per-payout breakdown
 * including financing paydowns.
 *
 * @param consolidatedPracticeId - Consolidated practice UUID/ID (from
 *   `currentPracticeObject.consolidatedPractice.id`).
 * @returns Promise resolving to `{ data: { clientSecret: string } }` — the
 *   client secret used to initialize the Stripe Connect embedded payouts
 *   component.
 */
export const createStripePayoutsAccountSession = (
	consolidatedPracticeId: string
): Promise<{ data: { clientSecret: string } }> =>
	createStripeAccountSession(consolidatedPracticeId, 'payouts')

/**
 * Reads the consolidated practice's Stripe Capital offer summary without
 * minting an embedded-components account session. Use for page-state queries
 * and the post-submit polling loop; the embed mints its session via
 * `createStripeCapitalAccountSession` inside Stripe Connect's
 * `fetchClientSecret` callback.
 *
 * @param consolidatedPracticeId - Consolidated practice ID.
 * @returns `data.offerId` (Stripe financing offer id), `data.offerAmount`
 *   (smallest currency unit), `data.offerStatus` (Stripe Capital status) — all
 *   null when no offer — and `data.promo`, the backend's classification of
 *   whether that status means "show the promo page" (false when no offer).
 */
export const getCapitalOfferStatus = (
	consolidatedPracticeId: string
): Promise<{ data: CapitalOfferSummary }> =>
	axiosClient.get(`/api/consolidated-practices/${consolidatedPracticeId}/capital-offer-status`)

/**
 * Records a Capital UI interaction event on the backend. Fire-and-forget:
 * tracking failures must never interrupt UX. Campaign attribution captured at
 * dashboard entry is merged into `metadata` here so no call site can forget it.
 *
 * @param consolidatedPracticeId - Consolidated practice ID.
 * @param eventType - Event from {@link CapitalEventType}.
 * @param metadata - Extra event fields. `attribution` is added as a sibling key
 *   and overwrites any the caller passes, so call sites must not set it.
 * @returns A promise resolving to whether the POST succeeded, for callers that
 *   only mark an event as sent once it lands.
 */
export const trackCapitalInteraction = (
	consolidatedPracticeId: string,
	eventType: CapitalEventType,
	metadata?: Record<string, unknown>
): Promise<boolean> => {
	const attribution = readCapitalAttribution()

	return axiosClient
		.post(`/api/consolidated-practices/${consolidatedPracticeId}/capital-interaction-events`, {
			eventType,
			metadata: { ...metadata, ...(attribution ? { attribution } : {}) }
		})
		.then(() => true)
		.catch(() => false)
}

/**
 * Adds a product (payments or lending) to an existing consolidated practice.
 * Used by consolidator admins from the Locations V2 page to enable new products.
 *
 * @param practiceId - The consolidated practice ID
 * @param product - The product to add: 'payments' or 'lending'
 * @param body - Additional configuration fields (e.g. clinicWebsite, numberOfTerminals for payments)
 * @returns The updated practice data from the backend
 */
export const addProductToPracticeV2 = (
	practiceId: string,
	product: 'payments' | 'lending',
	body: Record<string, unknown>
) => axiosClient.post(`/api/consolidated-practices/${practiceId}/products/${product}`, body)

/**
 * Updates an existing consolidated practice via PATCH.
 * Used by the Edit Practice v2 page to save changed fields only.
 *
 * @param id - The consolidated practice ID
 * @param payload - Partial object with only the changed fields
 * @returns AxiosResponse with the updated consolidated practice
 */
export const updateConsolidatedPractice = (
	id: string,
	payload: Record<string, unknown>
): Promise<{ data: unknown }> => axiosClient.patch(`/api/consolidated-practices/${id}`, payload)

/**
 * Soft-deletes a consolidated practice. The backend marks the practice as
 * deleted and rejects the linked Stripe account when applicable.
 *
 * @param id - The consolidated practice ID to soft-delete
 * @returns AxiosResponse confirming the deletion
 */
export const deleteConsolidatedPractice = (id: string): Promise<{ data: unknown }> =>
	axiosClient.delete(`/api/consolidated-practices/${id}`)

/**
 * Fetches a single consolidated practice by its paymentId (stripeAccountId).
 * Works at practice level — the backend validates user membership via
 * `userBelongsToConsolidatedPractice` middleware.
 *
 * @param stripeAccountId - The Stripe account ID (acct_*) to look up.
 * @returns The consolidated practice data, or null if not found.
 */
export const getConsolidatedPracticeByPaymentId = async (
	stripeAccountId: string
): Promise<ConsolidatedPractice | null> => {
	try {
		const { data } = await axiosClient.get<{
			data: { consolidatedPractice: ConsolidatedPractice }
		}>(`/api/consolidated-practices/${stripeAccountId}?idType=paymentId`)
		return data?.consolidatedPractice ?? null
	} catch (error) {
		if (axios.isAxiosError(error) && error.response?.status === 404) return null
		throw error
	}
}
